import { Coffee, Beer, Wine, Utensils, Music, Dog, Car, CreditCard, Baby, Salad,Martini, Cake } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { RestaurantSection } from "@/components/restaurants/restaurant-section";

interface RestaurantFeaturesProps {
  features: {
    takeout?: boolean;
    delivery?: boolean;
    dineIn?: boolean;
    curbsidePickup?: boolean;
    reservable?: boolean;
    servesBreakfast?: boolean;
    servesBrunch?: boolean;
    servesLunch?: boolean;
    servesDinner?: boolean;
    servesBeer?: boolean;
    servesWine?: boolean;
    servesVegetarianFood?: boolean;
    outdoorSeating?: boolean;
    liveMusic?: boolean;
    menuForChildren?: boolean;
    servesCocktails?: boolean;
    servesDessert?: boolean;
    servesCoffee?: boolean;
    allowsDogs?: boolean;
    paymentOptions?: any;
    parkingOptions?: any;
  };
}

export function RestaurantFeatures({ features }: RestaurantFeaturesProps) {
  // Only show sections that have at least one true value
  const serviceFeatures = [
    { key: 'takeout', label: 'Takeout Available' },
    { key: 'delivery', label: 'Delivery Available' },
    { key: 'dineIn', label: 'Dine-in' },
    { key: 'curbsidePickup', label: 'Curbside Pickup' },
    { key: 'reservable', label: 'Accepts Reservations' },
  ].filter(feature => features[feature.key as keyof typeof features]);

  const diningFeatures = [
    { key: 'servesBreakfast', label: 'Breakfast', icon: Utensils },
    { key: 'servesBrunch', label: 'Brunch', icon: Utensils },
    { key: 'servesLunch', label: 'Lunch', icon: Utensils },
    { key: 'servesDinner', label: 'Dinner', icon: Utensils },
  ].filter(feature => features[feature.key as keyof typeof features]);

  const beverageFeatures = [
    { key: 'servesCoffee', label: 'Coffee', icon: Coffee },
    { key: 'servesBeer', label: 'Beer', icon: Beer },
    { key: 'servesWine', label: 'Wine', icon: Wine },
    { key: 'servesCocktails', label: 'Cocktails', icon: Martini },
  ].filter(feature => features[feature.key as keyof typeof features]);

  const amenities = [
    { key: 'outdoorSeating', label: 'Outdoor Seating', icon: Utensils },
    { key: 'liveMusic', label: 'Live Music', icon: Music },
    { key: 'menuForChildren', label: "Kids' Menu", icon: Baby },
    { key: 'allowsDogs', label: 'Dog Friendly', icon: Dog },
    { key: 'servesVegetarianFood', label: 'Vegetarian Options', icon: Salad },
    { key: 'servesDessert', label: 'Desserts', icon: Cake },
  ].filter(feature => features[feature.key as keyof typeof features]);

  // Only render sections that have items
  if (!serviceFeatures.length && !diningFeatures.length && 
      !beverageFeatures.length && !amenities.length) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-6">
          {serviceFeatures.length > 0 && (
            <RestaurantSection id="service-options">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Service Options</h3>
              <div className="flex flex-wrap gap-2">
                {serviceFeatures.map(feature => (
                  <span 
                    key={feature.key}
                    className="inline-flex items-center px-2.5 py-1.5 md:py-2.5 rounded-full text-xs md:text-sm font-medium bg-secondary text-secondary-foreground"
                  >
                    {feature.label}
                  </span>
                ))}
              </div>
            </RestaurantSection>
          )}

          {diningFeatures.length > 0 && (
            <RestaurantSection id="meals-served">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Meals Served</h3>
              <div className="flex flex-wrap gap-2">
                {diningFeatures.map(feature => (
                  <span 
                    key={feature.key}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 md:py-2.5 rounded-full text-xs md:text-sm font-medium bg-secondary text-secondary-foreground"
                  >
                    <feature.icon className="w-3 h-3" />
                    {feature.label}
                  </span>
                ))}
              </div>
            </RestaurantSection>
          )}

          {beverageFeatures.length > 0 && (
            <RestaurantSection id="beverages">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Beverages</h3>
              <div className="flex flex-wrap gap-2">
                {beverageFeatures.map(feature => (
                  <span 
                    key={feature.key}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 md:py-2.5 rounded-full text-xs md:text-sm font-medium bg-secondary text-secondary-foreground"
                  >
                    <feature.icon className="w-3 h-3" />
                    {feature.label}
                  </span>
                ))}
              </div>
            </RestaurantSection>
          )}

          {amenities.length > 0 && (
            <RestaurantSection id="amenities">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {amenities.map(feature => (
                  <span 
                    key={feature.key}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 md:py-2.5 rounded-full text-xs md:text-sm font-medium bg-secondary text-secondary-foreground"
                  >
                    <feature.icon className="w-3 h-3" />
                    {feature.label}
                  </span>
                ))}
              </div>
            </RestaurantSection>
          )}
        </div>
      </CardContent>
    </Card>
  );
} 